import * as React from 'react'
import styled from 'styled-components'

import * as api from '@owl-nest/api-client/latest'
import { t } from '@owl-nest/localize'
import * as plume from '@ulule/owl-kit-components/next'

import { ActivityCard } from './ActivityCard'
import { DashedArrow, DashedArrowXS } from './Vectors'

const ICON_CONTAINER_CLASSNAME = 'hover-icon'
const ICON_CONTAINER_SELECTOR = `.${ICON_CONTAINER_CLASSNAME}`

const ICON_SIZE = plume.utils.isMobile() ? 24 : 32

type ActivityCardListProps = {
  userLocale: api.Lang
}

export function ActivityCardList({ userLocale }: ActivityCardListProps): React.ReactElement {
  return (
    <CardListWrapper>
      <CardListTitleSection>
        <plume.styles.heading.L asDesktop="XL">{t('Ulule allows you to')}</plume.styles.heading.L>
        <DashedArrow />
        <DashedArrowXS />
      </CardListTitleSection>
      {activityItems.map((item, index) => (
        <ActivityCard
          key={index}
          description={item.description()}
          icon={item.icon}
          iconContainerClassname={ICON_CONTAINER_CLASSNAME}
          link={typeof item.link === 'string' ? item.link : item.link(userLocale)}
          title={item.title()}
        />
      ))}
    </CardListWrapper>
  )
}

const activityItems = [
  {
    description: () =>
      t(
        'What makes us different? A team that advises you throughout your fundraising process. 8 out of 10 projects reach and often exceed their goal!',
      ),
    link: (userLocale: api.Lang) => `https://${userLocale === 'fr' ? 'fr' : 'www'}.ulule.com/projects/create/`,
    icon: <plume.glyphs.colored.Egg tint="blue" hoverSelector={ICON_CONTAINER_SELECTOR} size={ICON_SIZE} />,
    title: () => t('Start a project or a pre-sale'),
  },
  {
    description: () =>
      t(
        'Creative, sustainable, cultural, ecological, solidarity-based, innovative: find and support an initiative you like among the hundreds of projects being funded on Ulule.',
      ),
    link: (userLocale: api.Lang) => `https://${userLocale === 'fr' ? 'fr' : 'www'}.ulule.com/discover`,
    icon: <plume.glyphs.colored.Search tint="blue" hoverSelector={ICON_CONTAINER_SELECTOR} size={ICON_SIZE} />,
    title: () => t('Explore projects and pre-sales'),
  },
  {
    description: () =>
      t(
        'Develop new skills in webmarketing, crowdfunding or impact entrepreneurship, progress in your professional life with a skills assessment. Our certified training courses are eligible for the CPF. This service is available in French only.',
      ),
    link: 'https://formation.ulule.com/',
    icon: <plume.glyphs.colored.Education tint="blue" hoverSelector={ICON_CONTAINER_SELECTOR} size={ICON_SIZE} />,
    title: () => t('Take action thanks to training courses'),
  },
  {
    description: () =>
      t(
        'Discover in our shop a selection of more than 8,000 products that are good for the planet and good for people. Many of them were first funded on Ulule! This service is available in French only.',
      ),
    link: 'https://boutique.ulule.com/',
    icon: <plume.glyphs.colored.Marketplace tint="blue" hoverSelector={ICON_CONTAINER_SELECTOR} size={ICON_SIZE} />,
    title: () => t('Adopt a sustainable approach to shopping'),
  },
  {
    description: () =>
      t(
        "Increase your company's impact by partnering with creative and sustainable projects. Train your employees on social and environmental impact.",
      ),
    link: 'https://partenaires.ulule.com/',
    icon: <plume.glyphs.colored.Partners tint="blue" hoverSelector={ICON_CONTAINER_SELECTOR} size={ICON_SIZE} />,
    title: () => t('Become a partner'),
  },
]

const CardListWrapper = styled.div`
  position: relative;
  max-width: 1088px;
  margin: 0 auto;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 52px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    gap: 56px 32px;
  }
`

const CardListTitleSection = styled.div`
  position: relative;
  text-align: center;
  margin-bottom: 22px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    text-align: left;
  }

  ${plume.styles.heading.L} {
    max-width: 335px;

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      max-width: 528px;
    }
  }

  ${DashedArrow} {
    display: none;

    @media screen and ${plume.BREAKPOINTS.LAPTOP} {
      display: block;
      position: absolute;
      top: 35%;
      right: 15%;
      transform: rotateY(0deg) rotateZ(0deg) scale(1);
    }
  }

  ${DashedArrowXS} {
    position: absolute;
    right: 10%;
    top: 65%;

    @media screen and ${plume.BREAKPOINTS.TABLET} {
      right: 45%;
      transform: rotateZ(-45deg) scale(1.5);
    }
    @media screen and ${plume.BREAKPOINTS.LAPTOP} {
      display: none;
    }
  }
`
